/**
 * Collapsible Component JavaScript
 * Handles the interactive behavior for collapsible elements
 */

class CollapsibleManager {
  constructor() {
    this.init();
  }

  init() {
    this.setupLiveRegion();
    this.setupCollapsibles();
  }

  setupLiveRegion() {
    // Create a live region for screen reader announcements if it doesn't exist
    if (!document.getElementById('collapsible-live-region')) {
      const liveRegion = document.createElement('div');
      liveRegion.id = 'collapsible-live-region';
      liveRegion.className = 'sr-only';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      document.body.appendChild(liveRegion);
    }
    this.liveRegion = document.getElementById('collapsible-live-region');
  }

  setupCollapsibles() {
    // Find all collapsible details elements
    const collapsibleElements = document.querySelectorAll('.collapsible-item');
    
    collapsibleElements.forEach((details) => {
      this.setupCollapsible(details);
    });
  }

  setupCollapsible(details) {
    const summary = details.querySelector('.collapsible-summary');
    if (!summary) return;
    
    // Find the plus and minus icon containers
    const plusIcon = summary.querySelector('.collapsible-icon-plus');
    const minusIcon = summary.querySelector('.collapsible-icon-minus');
    
    // Set initial ARIA state
    this.updateAriaState(details, summary);
    
    // Listen for toggle events
    details.addEventListener('toggle', () => {
      this.updateAriaState(details, summary);
      this.updateIconVisibility(details, plusIcon, minusIcon);
      this.announceStateChange(details, summary);
    });

    // Set initial icon visibility
    this.updateIconVisibility(details, plusIcon, minusIcon);
  }

  updateAriaState(details, summary) {
    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    
    // Update aria-label dynamically
    const title = summary.querySelector('.collapsible-title');
    if (title) {
      const titleText = title.textContent.trim();
      const action = isOpen ? 'Collapse' : 'Expand';
      summary.setAttribute('aria-label', `${action} ${titleText} section`);
    }
  }

  updateIconVisibility(details, plusIcon, minusIcon) {
    if (!plusIcon || !minusIcon) return;
    
    if (details.open) {
      plusIcon.style.opacity = '0';
      minusIcon.style.opacity = '1';
    } else {
      plusIcon.style.opacity = '1';
      minusIcon.style.opacity = '0';
    }
  }

  announceStateChange(details, summary) {
    if (!this.liveRegion) return;
    
    const title = summary.querySelector('.collapsible-title');
    if (title) {
      const titleText = title.textContent.trim();
      const state = details.open ? 'expanded' : 'collapsed';
      this.liveRegion.textContent = `${titleText} section ${state}`;
      
      // Clear the announcement after a short delay
      setTimeout(() => {
        this.liveRegion.textContent = '';
      }, 1000);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new CollapsibleManager();
});

// Re-initialize if new collapsibles are added dynamically
document.addEventListener('collapsible:refresh', () => {
  new CollapsibleManager();
});

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CollapsibleManager;
}
